/**
 * Hooks for determining user's team membership and auto-defaulting team filter.
 */
import { useMemo } from 'react';
import { useUserTeamIds as useSessionTeamIds, useIsLeadership } from '@/hooks/useSession';

const TEAM_STORAGE_KEY = 'kpi-scorecard-team-id';

/**
 * Returns the current user's team IDs (sourced from session pgEnrich).
 */
export function useUserTeams() {
  const userTeamIds = useSessionTeamIds();
  return { userTeamIds, isLoading: false };
}

/**
 * Returns the default team ID for the scorecard filter.
 *
 * Priority:
 * 1. localStorage persisted selection
 * 2. Leadership members -> null (show all teams)
 * 3. User with exactly 1 team -> that team ID
 * 4. User with 0 or 2+ teams -> null (show all their teams)
 */
export function useDefaultTeamId() {
  const { userTeamIds, isLoading } = useUserTeams();
  const isLeader = useIsLeadership();

  const defaultTeamId = useMemo(() => {
    // Check localStorage first
    const stored = localStorage.getItem(TEAM_STORAGE_KEY);
    if (stored) return stored;

    // Leadership members see every team by default
    if (isLeader) return null;

    // Users with exactly one team default to that team
    if (userTeamIds.length === 1) return userTeamIds[0];

    // Otherwise show all
    return null;
  }, [userTeamIds, isLeader]);

  return { defaultTeamId, isLoading };
}

/**
 * Persist team selection to localStorage.
 */
export function persistTeamSelection(teamId: string | null) {
  if (teamId) {
    localStorage.setItem(TEAM_STORAGE_KEY, teamId);
  } else {
    localStorage.removeItem(TEAM_STORAGE_KEY);
  }
}
